import React from 'react';
import styled from 'styled-components';
import { default as ReactAvatar } from 'react-avatar';
import { useKidsChurchChatUsers } from '../../hooks/useKidsChurchChatUsers';
import { useAuth } from '../../context/AuthContext';

const ListContainer = styled.div`
  width: 300px;
  border-right: 1px solid var(--outline);
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;

const SearchWrap = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--outline);
  background: var(--surface);
  position: sticky;
  top: 0;
  z-index: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.45rem 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.85rem;
`;

const Item = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  background: ${({ selected }) => (selected ? 'var(--surfaceVariant)' : 'transparent')};
  overflow: hidden;
  &:hover {
    background: var(--surfaceVariant);
  }
`;

const TextContainer = styled.div`
  flex: 1;
  margin-left: 0.75rem;
  min-width: 0;
`;

const Name = styled.div`
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LastMessage = styled.div`
  font-size: 0.75rem;
  color: var(--onSurfaceVariant);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TimeText = styled.div`
  font-size: 0.65rem;
  color: var(--onSurfaceVariant);
  margin-left: 0.5rem;
`;

const Chip = styled.span<{ variant: 'parent' | 'staff' }>`
  font-size: 0.6rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 9999px;
  background: ${({ variant }) => (variant === 'parent' ? 'var(--successContainer)' : 'var(--surfaceVariant)')};
  color: ${({ variant }) => (variant === 'parent' ? 'var(--success)' : 'var(--primary)')};
  border: 1px solid
    ${({ variant }) => (variant === 'parent' ? 'var(--success)' : 'var(--accent)')};
`;

interface Props {
  selectedId: string | null;
  onSelect: (id: string) => void;
  includeUserIds?: string[];
}

export const ChatList: React.FC<Props> = ({ selectedId, onSelect, includeUserIds }) => {
  const { user } = useAuth();
  const { data: users = [], isLoading } = useKidsChurchChatUsers(
    user?.id || '',
    includeUserIds ?? []
  );
  const [search, setSearch] = React.useState('');

  if (isLoading) return <div style={{ padding: '1rem' }}>Loading...</div>;

  const normalizedSearch = search.trim().toLowerCase();
  const filteredUsers = normalizedSearch
    ? users.filter((u) => u.fullName?.toLowerCase().includes(normalizedSearch))
    : users;

  return (
    <ListContainer>
      <SearchWrap>
        <SearchInput
          placeholder="Search users"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </SearchWrap>
      {filteredUsers.map((u) => (
        <Item key={u.id} selected={u.id === selectedId} onClick={() => onSelect(u.id)}>
          <ReactAvatar name={u.fullName} src={u.avatar} size="40" round />
          <TextContainer>
            <Name>{u.fullName}</Name>
            <MetaRow>
              <LastMessage>{u.lastMessage || 'No messages yet'}</LastMessage>
              {u.lastMessageTime && (
                <TimeText>
                  {new Date(u.lastMessageTime).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}
                </TimeText>
              )}
            </MetaRow>
            {u.isParent ? (
              <Chip variant="parent">Parent</Chip>
            ) : (
              <Chip variant="staff">Staff</Chip>
            )}
          </TextContainer>
          {u.unreadCount > 0 && (
            <span
              style={{
                background: 'var(--error)',
                color: 'white',
                borderRadius: '9999px',
                padding: '0 6px',
                fontSize: '12px',
                marginLeft: 'auto',
              }}
            >
              {u.unreadCount}
            </span>
          )}
        </Item>
      ))}
    </ListContainer>
  );
};
